import React, { useState } from "react";
import { Box, Button, Grid, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { FormProvider, RHFTextField } from "../../../common/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup"
import { apiConstants, post, postGeneric } from "../../../common/constants";
import { useNavigate, useLocation } from "react-router-dom";
import { useUnsavedChangesBlocker } from "../../../helpers/UtilityHelper";
import CustomerDetails from "./CustomerDetails"
import PickupLocation from "./PickupLocation"
import OrderDetails from "./OrderDetails";
import PackageDetails from "./PackageDetails";
import { useGetMerchantQuery } from "../../../redux/commonRTK";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import PageTitle from "../../../common/PageTitle";
import { useSnackbar } from "notistack";
import SelectCourier from "./Courier";
import { exclude } from "../list2";
import Loader from "../../../common/Loader";
import { CardHeader, TextField } from '@mui/material'
import jwt_decode from 'jwt-decode'
import { getToken } from '../../../helpers/ReduxHelpers'
import { useCallback, useMemo, useEffect } from 'react'
import { VisibilityTwoTone } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import MUIModal from '../../../common/MUIModal'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, BORDER, WHITE, PRIMARY_BLUE, SUBTLE } from '../../../styles/foundation/colors';
import {
    pageContainerSx, stepperCardSx, stepTitleSx, primaryBtnSx,
    helpTextSx, helpTextTypeSx, filterBarSx,
} from '../../../styles/patterns';


const steps = [
    "Pickup Details",
    "Drop Details",
    "Order Details",
    "Package Details",
    "Select Courier",
];

// `embedded`: rendered inside the tabbed Create Shipment page, which owns the page container.
function CreateB2COrderRVP({ embedded = false }) {
    const merchant = getMerchant();
    const [activeStep, setActiveStep] = React.useState(0);
    const navigate = useNavigate();
    const [creatingOrder, setIsCreatingOrder] = React.useState(false);
    const { data } = useGetMerchantQuery({ id: merchant.id });
    const [response, setResponse] = useState(null)
    const [awbLoading, setAwbLoading] = useState(false)
    const pickupLocations = data?.pickup_details || [];
    const [showPiiDetails, setShowPiiDetails] = useState(null)
    const [showButton, setShowButton] = useState(null)
    const { enqueueSnackbar } = useSnackbar();
    const methods = useForm({
        defaultValues: {
            reference: '',
            payment_mode: 'PREPAID',
            order_type: 'Reverse Shipment',
            pickup_details: {
                from_phone_number: '', from_name: '', from_email: '',
                from_pincode: '', from_city: '', from_state: '', from_address: '',
            },
            client_order_id: '', invoice_value: '',
            shipping_charge: '', transaction_charge: '', giftwrap_charge: '',
            shipment_detail: [{ item_weight: '', item_length: '', item_breadth: '', item_height: '' }],
            courier_type: 'Prozo Choice',
            qc_type: '',
        },
        resolver: yupResolver(
            Yup.object().shape({
                pickup_details: Yup.object().shape({
                    from_phone_number: Yup.string()
                        .required("Please enter your phone number.")
                        .test('phone-or-masked', "Your phone number must be exactly 10 digits long.", function (value) {
                            if (!value) return true;
                            if (value.includes('*')) return true;
                            return /^\d{10}$/.test(value);
                        }),
                    from_name: Yup.string().required("Please enter your name."),
                    from_email: Yup.string()
                        .required("Please enter your email address.")
                        .test('email-or-masked', "Please enter a valid email address.", function (value) {
                            if (!value) return true;
                            if (value.includes('*')) return true;
                            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                        }),
                    from_pincode: Yup.string().required("Please enter your pincode.").matches(/^\d{6}$/, "Pincode must be exactly 6 digits long."),
                    from_city: Yup.string().required("Please enter your city."),
                    from_state: Yup.string().required("Please enter your state."),
                    from_address: Yup.string().required("Please enter your address."),
                }),
                item_list: Yup.array().of(
                    Yup.object().shape({
                        item_name: Yup.string().required("Please enter the item name."),
                        units: Yup.number().min(1, "Quantity must be at least 1.").required("Please enter the quantity."),
                        sku_id: Yup.string().required("Please enter the SKU ID."),
                        selling_price: Yup.number()
                            .transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; })
                            .nullable().min(1, "Selling price must be at least Rs. 1.").required("Please enter the selling price."),
                        discount: Yup.number().optional(), tax: Yup.number().optional(), hsn: Yup.string().optional(),
                    })
                ),
                cod_amount: Yup.number().when('payment_mode', {
                    is: val => val === 'COD',
                    then: Yup.number()
                        .transform((v, o) => { const s = String(o).replace(/[\s]+/g, ''); return s.match(/^[0-9]*$/) ? Number(s) : null; })
                        .typeError("COD amount must be a numeric value.")
                        .required("COD amount is required when payment mode is COD.")
                        .min(1, "COD amount must be at least Rs. 1.")
                        .max(Yup.ref('invoice_value'), "COD amount cannot be greater than the invoice value.")
                }),
                rvp_reason: Yup.string().when('order_type', {
                    is: val => val === 'Reverse Shipment',
                    then: Yup.string().required("RVP reason is mandatory for Reverse Shipment."),
                }),
                qc_type: Yup.string().required("Please select a QC type."),
                payment_mode: Yup.string().required("Please select a payment mode."),
                reference: Yup.string().required("Please enter a reference number."),
                pickupLocation: Yup.string().required("Please enter the pickup location."),
                invoice_value: Yup.number()
                    .transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; })
                    .nullable().min(1, "Invoice amount must be at least Rs. 1.").required("Please enter the invoice value."),
                shipping_charge: Yup.string().optional(),
                transaction_charge: Yup.string().optional(),
                giftwrap_charge: Yup.string().optional(),
                shipment_detail: Yup.array().of(
                    Yup.object().shape({
                        item_weight: Yup.number().transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; }).nullable().min(1, "Weight must be at least 1 gram.").required("Weight is required."),
                        item_length: Yup.number().transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; }).nullable().min(1, "Length must be at least 1 cm.").required("Length is required."),
                        item_breadth: Yup.number().transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; }).nullable().min(1, "Breadth must be at least 1 cm.").required("Breadth is required."),
                        item_height: Yup.number().transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; }).nullable().min(1, "Height must be at least 1 cm.").required("Height is required.")
                    })
                ).required("Please provide shipment details.")
            })
        )
    });
    const { handleSubmit, reset, formState: { isDirty } } = methods;
    const { BlockerDialog } = useUnsavedChangesBlocker(isDirty, reset);

    React.useEffect(() => {
        if (response && response?.delivery_details) {
            methods.setValue('pickup_details.from_phone_number', showPiiDetails ? showPiiDetails?.phone : response?.delivery_details.to_phone_number || '');
            methods.setValue('pickup_details.from_email', showPiiDetails ? showPiiDetails?.email : response?.delivery_details.to_email || '');
            methods.setValue('pickup_details.from_name', response?.delivery_details.to_name || '');
            methods.setValue('pickup_details.from_pincode', response?.delivery_details.to_pincode || '');
            methods.setValue('pickup_details.from_city', response?.delivery_details.to_city || '');
            methods.setValue('pickup_details.from_state', response?.delivery_details.to_state || '');
            methods.setValue('pickup_details.from_address', response?.delivery_details.to_address || '');
            methods.setValue('shipment_detail[0].item_weight', response?.shipment_detail[0]?.item_weight);
            methods.setValue('shipment_detail[0].item_length', response?.shipment_detail[0]?.item_length);
            methods.setValue('shipment_detail[0].item_breadth', response?.shipment_detail[0]?.item_breadth);
            methods.setValue('shipment_detail[0].item_height', response?.shipment_detail[0]?.item_height);
        } else { reset() }
    }, [response, methods, showPiiDetails]);

    const onSubmit = (formData) => {
        setIsCreatingOrder(true);
        let request = { ...formData, dispatch_mode: null, merchantOId: merchant.id };
        const selectedAddress = pickupLocations.find(loc => loc.id === request.pickupLocation);
        const delivery_details = {
            to_address: selectedAddress.address_line, to_city: selectedAddress.city,
            to_country: selectedAddress.country, to_name: selectedAddress.name,
            to_pincode: selectedAddress.pincode, to_state: selectedAddress.state,
            to_phone_number: selectedAddress.contactNumber, to_email: selectedAddress.email,
            gstin: selectedAddress.gstin
        }

        if (request.payment_mode === "PREPAID") request.cod_amount = 0;
        request.skipRule = request.courier_type === "Other";
        if (request.courier_type !== "Other") { delete request.courierPartner; }
        else { request.courier_id = { prozoId: request.courierPartner }; delete request.courierPartner; }
        request.shippingLabelId = request.shippingLabelId || request.reference;
        request.channel_name = request.qc_type === 'doorstep' ? 'QC' : 'NONQC';
        if (request.qc_type === 'NO') delete request.qc_type;

        request = { ...request, delivery_details };
        request.pickup_details.from_country = 'IN';
        request.is_reverse = true;

        if (response?.reference && response?.awb_number && response?.order_type === "FORWARD SHIPMENT") {
            request.forwardOrderReference = response?.reference;
        }

        postGeneric(apiConstants.CREATE_ORDER, request)
            .then((response) => {
                if (response?.data?.meta?.message?.includes("Order already placed with reference number")) {
                    enqueueSnackbar("Reference number already exists", { variant: 'error' });
                } else if (response?.data?.meta?.message?.includes("Order created with reference number")) {
                    enqueueSnackbar("Successfully Created!", { variant: 'success' });
                    reset();
                    if (request.order_type === "Forward Shipment") navigate("/orders");
                    else navigate("/reverse-orders");
                }
            }).catch((err) => {
                const message = err?.response?.data?.message || err.message;
                enqueueSnackbar(message || "Error creating order", { variant: "error" });
            }).finally(() => setIsCreatingOrder(false))
    }

    return (
        <Box sx={embedded ? undefined : pageContainerSx}>
            {awbLoading && <Loader />}
            <PageTitle subTitle="Set up a reverse pickup for delivered order items">Create Reverse Shipment</PageTitle>

            {/* AWB search */}
            <OrderSearchAWB setResponse={setResponse} setAwbLoading={setAwbLoading} setShowButton={setShowButton} />

            {/* Help text */}
            <Box sx={helpTextSx}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
                <Typography sx={helpTextTypeSx}>
                    <strong>How it works:</strong> Enter the forward shipment AWB above to auto-fill customer details.
                    Then complete pickup location, order details, package dimensions, and select a courier for the return.
                </Typography>
            </Box>

            <Grid container spacing={2.5} sx={{ alignItems: 'flex-start' }}>
                {/* Stepper sidebar */}
                <Grid item xs={12} md={2.5}>
                    <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', p: 2 }}>
                        <Stepper orientation="vertical" activeStep={activeStep}
                            sx={{
                                '& .MuiStepLabel-label': { fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.medium },
                                '& .MuiStepLabel-label.Mui-active': { fontWeight: WEIGHT.bold, color: PRIMARY_BLUE },
                                '& .MuiStepLabel-label.Mui-completed': { color: SUBTLE },
                                '& .MuiStepIcon-root.Mui-active': { color: PRIMARY_BLUE },
                            }}
                        >
                            {steps.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
                        </Stepper>
                    </Box>
                </Grid>

                {/* Form area */}
                <Grid item xs={12} md={9.5}>
                    <Box sx={stepperCardSx}>
                        <Typography sx={stepTitleSx}>{activeStep + 1}. {steps[activeStep]}</Typography>
                        {activeStep === 0 && (
                            <PIIDetails merchantOid={merchant.id} reference={response?.reference}
                                awbNumber={response?.awb_number} isReverseShipment={true}
                                setShowPiiDetails={setShowPiiDetails} showButton={showButton}
                            />
                        )}
                        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                            {activeStep === 0 && <CustomerDetails setActiveStep={setActiveStep} details={response?.pickup_details} />}
                            {activeStep === 1 && <PickupLocation setActiveStep={setActiveStep} response={response} />}
                            {activeStep === 2 && <OrderDetails setActiveStep={setActiveStep} response={response} reset={reset} />}
                            {activeStep === 3 && <PackageDetails creatingOrder={creatingOrder} setActiveStep={setActiveStep} />}
                            {activeStep === 4 && (
                                <SelectCourier creatingOrder={creatingOrder} setActiveStep={setActiveStep}
                                    formData={methods.getValues()} pickupLocations={pickupLocations} merchantId={merchant.id}
                                />
                            )}
                        </FormProvider>
                    </Box>
                </Grid>
            </Grid>
            <BlockerDialog />
        </Box>
    )
}

export default CreateB2COrderRVP;

function OrderSearchAWB({ setResponse, setAwbLoading, setShowButton }) {
    const excludes = exclude;
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    // Populated by the "Create Reverse Order" button on the forward shipment's detail page
    // (?awb=<awb>) -- only the AWB is passed through the URL; everything else is re-fetched
    // here via the normal lookup so it can never go stale against the order's current state.
    const awbFromQuery = useMemo(() => new URLSearchParams(location.search).get('awb') || '', [location.search])
    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({ awb: Yup.string().required("AWB Number is required") })),
        defaultValues: { awb: awbFromQuery },
    });
    const { handleSubmit } = methods;

    const onSubmit = (data) => {
        setAwbLoading(true)
        post(`${apiConstants.BASE_LOCAL}${apiConstants.ORDER_LISTS}`, {
            excludes,
            andfilter: [{ field: 'awbNumber', operator: 'eq', value: data.awb }],
        }).then((response) => {
            setAwbLoading(false)
            if (response?.data?.count > 0 && response?.data?.data[0]?.orderStatus === "DELIVERED" && response?.data?.data[0]?.order_type !== "REVERSE SHIPMENT") {
                setResponse(response?.data?.data[0])
                setShowButton(response?.data)
            } else if (response?.data?.count > 0 && response?.data?.data[0]?.orderStatus !== "DELIVERED" && response?.data?.data[0]?.order_type !== "REVERSE SHIPMENT") {
                setResponse([])
                setShowButton(response?.data)
                enqueueSnackbar(`Reverse shipment cannot be created as the order status is "${response?.data?.data[0]?.orderStatus}". Reverse shipments are only allowed for delivered orders.`, { variant: 'error' });
            } else if (response?.data?.count > 0 && response?.data?.data[0]?.order_type === "REVERSE SHIPMENT") {
                setResponse([]); setShowButton(response?.data)
                enqueueSnackbar("Order is already of type Reverse", { variant: 'error' });
            } else {
                setResponse([]); setShowButton(response?.data)
                enqueueSnackbar("Invalid AWB", { variant: 'error' });
            }
        }).catch((error) => {
            setAwbLoading(false); setShowButton(null); setResponse(null)
            enqueueSnackbar("Some error occurred", { variant: 'error' });
            console.error(error);
        })
    }

    // Auto-run the lookup once when arriving via a deep link (?awb=...) so the seller lands
    // straight on the populated form instead of having to re-type/paste the AWB they came from.
    useEffect(() => {
        if (awbFromQuery) onSubmit({ awb: awbFromQuery })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awbFromQuery])

    return (
        <Box sx={filterBarSx}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY, mb: 1.5 }}>
                Search Forward AWB
            </Typography>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <RHFTextField variant="outlined" name="awb" label="AWB Number" size="small" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button variant="contained" size="small" type="submit" sx={primaryBtnSx} startIcon={<SearchIcon />}>Search</Button>
                    </Grid>
                </Grid>
            </FormProvider>
        </Box>
    )
}

const OTP_LENGTH = 6

function PIIDetails(props) {
    const { merchantOid, reference, awbNumber, title, setShowPiiDetails, showPiiDetails, showButton } = props
    const piiAccessAllow = useMemo(() => {
        try { return jwt_decode(getToken())?.piiAccessAllow } catch { return false }
    }, [])

    const [loading, setLoading] = useState(false)
    const [tokenId, setTokenId] = useState(null)
    const [otp, setOtp] = useState('')
    const [otpLoading, setOtpLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar()

    const handleShowPii = useCallback(async () => {
        try {
            setLoading(true)
            const response = await postGeneric('orderdata/send-otp', { merchantOid, reference, awbNumber })
            setTokenId(response?.data?.tokenId)
        } catch (error) {
            setTokenId(null)
            enqueueSnackbar(error?.response?.data?.message, { variant: 'error' })
        } finally { setLoading(false) }
    }, [merchantOid, reference, awbNumber, enqueueSnackbar])

    const handleOtpChange = useCallback((e) => {
        const value = e.target.value
        if (value === '' || (/^\d+$/.test(value) && value.length <= OTP_LENGTH)) setOtp(value)
    }, [])

    const handleOtpSubmit = useCallback(async () => {
        const trimmedOtp = otp.trim()
        if (!trimmedOtp) { enqueueSnackbar('Please enter OTP', { variant: 'error' }); return }
        if (trimmedOtp.length !== OTP_LENGTH) { enqueueSnackbar(`OTP must be ${OTP_LENGTH} digits`, { variant: 'error' }); return }
        if (!tokenId) { enqueueSnackbar('Token ID is missing', { variant: 'error' }); return }

        try {
            setOtpLoading(true)
            const response = await postGeneric('orderdata/getdata', { merchantOid, reference, awbNumber, tokenId, otp: trimmedOtp })
            if (response?.data) {
                setShowPiiDetails({ email: response.data?.deliveryEmail || '', phone: response.data?.deliveryPhone || '' })
                setTokenId(null); setOtp('')
                enqueueSnackbar('PII data retrieved successfully', { variant: 'success' })
            }
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to verify OTP', { variant: 'error' })
        } finally { setOtpLoading(false) }
    }, [otp, tokenId, merchantOid, reference, awbNumber])

    const handleCloseOtpModal = useCallback(() => { setTokenId(null); setOtp('') }, [])
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !otpLoading && otp.trim().length === OTP_LENGTH) handleOtpSubmit()
    }, [otp, otpLoading, handleOtpSubmit])

    return (
        <>
            <CardHeader title={title}
                action={
                    piiAccessAllow && !showPiiDetails && showButton?.data?.length > 0 ? (
                        <LoadingButton onClick={handleShowPii} loading={loading} color='error' size='small' variant='contained' startIcon={<VisibilityTwoTone />}>
                            Show PII
                        </LoadingButton>
                    ) : null
                }
            />
            <MUIModal open={tokenId !== null} handleClose={handleCloseOtpModal} title='Enter OTP' maxWidth='sm' hideClose={false}
                action={
                    <LoadingButton onClick={handleOtpSubmit} loading={otpLoading} variant='contained' color='primary' disabled={otp.trim().length !== OTP_LENGTH}>
                        Verify OTP
                    </LoadingButton>
                }
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant='body2' sx={{ mb: 2, fontFamily: FONT }}>
                        Please enter the {OTP_LENGTH}-digit OTP sent to your registered phone number to view PII details.
                    </Typography>
                    <TextField fullWidth label='OTP' value={otp} onChange={handleOtpChange}
                        placeholder={`Enter ${OTP_LENGTH}-digit OTP`} variant='outlined' type='tel'
                        inputMode='numeric' autoComplete='one-time-code' autoFocus maxLength={OTP_LENGTH}
                        inputProps={{ pattern: '[0-9]*', maxLength: OTP_LENGTH }} onKeyDown={handleKeyDown}
                        helperText={otp.length > 0 && otp.length < OTP_LENGTH ? `${OTP_LENGTH - otp.length} digit(s) remaining` : ''}
                    />
                </Box>
            </MUIModal>
        </>
    )
}
